<template>
  <v-main>
    <v-container fluid>
      <v-card elevation="6" rounded="xxl">
        <v-card-title class="mt-4">
          <v-btn
            class="mx-2"
            fab
            x-small
            color="primary"
            @click="$router.back()"
          >
            <v-icon dark> mdi-arrow-left </v-icon>
          </v-btn>
          Add Seat Configuration
        </v-card-title>
        <v-card-text>
          <v-form ref="form" v-model="valid">
            <v-row>
              <v-col sm="6">
                <v-text-field
                  v-model="form.name"
                  outlined
                  label="Configuration Name *"
                  prepend-inner-icon="mdi-tag"
                  :rules="[rules.required]"
                  placeholder="e.g., 10 Seats - 2 VIP"
                ></v-text-field>
              </v-col>

              <v-col sm="3">
                <v-select
                  v-model="form.total_seats"
                  outlined
                  label="Total Seats *"
                  :items="seatOptions"
                  prepend-inner-icon="mdi-seat"
                  :rules="[rules.required]"
                  @change="validateVipSeats"
                ></v-select>
              </v-col>

              <v-col sm="3">
                <v-text-field
                  v-model.number="form.vip_seats"
                  outlined
                  label="VIP Seats *"
                  prepend-inner-icon="mdi-crown"
                  type="number"
                  min="0"
                  :max="form.total_seats || 20"
                  :rules="[rules.required, rules.vipSeatsValid]"
                  @input="updateVipPositions"
                ></v-text-field>
              </v-col>

              <v-col sm="12">
                <v-textarea
                  v-model="form.description"
                  outlined
                  label="Description (Optional)"
                  prepend-inner-icon="mdi-text"
                  rows="3"
                  placeholder="Brief description of this configuration"
                ></v-textarea>
              </v-col>

              <!-- VIP Seat Positions -->
              <v-col sm="12" v-if="form.vip_seats > 0">
                <v-card outlined>
                  <v-card-title class="subtitle-1">
                    <v-icon left color="amber darken-2">mdi-crown</v-icon>
                    Select VIP Seat Positions ({{ form.vip_seats }} seats)
                  </v-card-title>
                  <v-card-text>
                    <v-alert
                      dense
                      outlined
                      type="info"
                      class="mb-3"
                      v-if="form.vip_seats > selectedVipPositions.length"
                    >
                      Please select {{ form.vip_seats }} seat position(s) for VIP
                    </v-alert>

                    <v-chip-group
                      v-model="selectedVipPositions"
                      column
                      multiple
                      active-class="amber darken-2 orange--text"
                    >
                      <v-chip
                        v-for="seat in availableSeats"
                        :key="`seat_${seat}`"
                        :value="seat"
                        filter
                        outlined
                      >
                        <v-icon left small>mdi-seat</v-icon>
                        Seat {{ seat }}
                      </v-chip>
                    </v-chip-group>

                    <v-alert
                      dense
                      type="warning"
                      v-if="selectedVipPositions.length !== form.vip_seats"
                      class="mt-3"
                    >
                      <strong>Warning:</strong> You must select exactly
                      {{ form.vip_seats }} seat position(s).
                      Currently selected: {{ selectedVipPositions.length }}
                    </v-alert>
                  </v-card-text>
                </v-card>
              </v-col>

              <!-- Preview -->
              <v-col sm="12" v-if="form.total_seats">
                <v-card outlined>
                  <v-card-title class="subtitle-1">
                    <v-icon left>mdi-eye</v-icon>
                    Preview
                  </v-card-title>
                  <v-card-text>
                    <div class="seat-preview">
                      <v-chip
                        v-for="seat in form.total_seats"
                        :key="`preview_${seat}`"
                        :color="
                          selectedVipPositions.includes(seat)
                            ? 'amber darken-2'
                            : 'grey lighten-2'
                        "
                        :text-color="
                          selectedVipPositions.includes(seat) ? 'white' : 'black'
                        "
                        class="ma-1"
                      >
                        <v-icon left small v-if="selectedVipPositions.includes(seat)">
                          mdi-crown
                        </v-icon>
                        <v-icon left small v-else>mdi-seat</v-icon>
                        {{ seat }}
                      </v-chip>
                    </div>
                  </v-card-text>
                </v-card>
              </v-col>

              <v-col sm="12">
                <v-switch
                  v-model="form.is_active"
                  label="Active"
                  color="success"
                  inset
                ></v-switch>
              </v-col>
            </v-row>
          </v-form>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="$router.back()"> Cancel </v-btn>
          <v-btn
            color="primary"
            :loading="loading"
            :disabled="!canSubmit"
            @click="submit"
          >
            Save Configuration
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-container>
  </v-main>
</template>

<script>
import axios from "axios";

export default {
  name: "SeatConfigurationCreate",
  data() {
    return {
      valid: false,
      loading: false,
      form: {
        name: "",
        total_seats: null,
        vip_seats: 0,
        vip_seat_positions: [],
        description: "",
        is_active: true,
      },
      selectedVipPositions: [],
      seatOptions: [
        // { text: "8 Seats", value: 8 },
        { text: "10 Seats", value: 10 },
        { text: "15 Seats", value: 15 },
        { text: "20 Seats", value: 20 },
      ],
      rules: {
        required: (value) => !!value || "This field is required",
        vipSeatsValid: (value) => {
          if (value < 0) return "VIP seats cannot be negative";
          if (value > this.form.total_seats)
            return `Cannot exceed total seats (${this.form.total_seats})`;
          return true;
        },
      },
    };
  },
  computed: {
    availableSeats() {
      if (!this.form.total_seats) return [];
      return Array.from({ length: this.form.total_seats }, (_, i) => i + 1);
    },
    canSubmit() {
      if (!this.valid) return false;
      if (this.form.vip_seats > 0) {
        return this.selectedVipPositions.length === this.form.vip_seats;
      }
      return true;
    },
  },
  watch: {
    selectedVipPositions(newVal) {
      // Create a copy to avoid mutating the watched array
      this.form.vip_seat_positions = [...newVal].sort((a, b) => a - b);
    },
  },
  methods: {
    validateVipSeats() {
      if (this.form.vip_seats > this.form.total_seats) {
        this.form.vip_seats = this.form.total_seats;
      }
      this.updateVipPositions();
    },

    updateVipPositions() {
      // If VIP seats is 0, clear selections
      if (this.form.vip_seats === 0) {
        this.selectedVipPositions = [];
        this.form.vip_seat_positions = [];
      }

      // If user reduced VIP count, trim selections
      if (this.selectedVipPositions.length > this.form.vip_seats) {
        this.selectedVipPositions = this.selectedVipPositions.slice(
          0,
          this.form.vip_seats
        );
      }
    },

    async submit() {
      if (!this.$refs.form.validate()) {
        alert("Please fill all required fields");
        return;
      }

      if (
        this.form.vip_seats > 0 &&
        this.selectedVipPositions.length !== this.form.vip_seats
      ) {
        alert(`Please select exactly ${this.form.vip_seats} VIP seat position(s)`);
        return;
      }

      this.loading = true;
      try {
        const payload = {
          ...this.form,
          vip_seat_positions:
            this.form.vip_seats > 0 ? this.form.vip_seat_positions : null,
        };

        const response = await axios.post("/seats", payload);

        if (response.data.success) {
          alert("Seat configuration created successfully");
          this.$router.push("/seat-configurations");
        }
      } catch (error) {
        console.error("Error creating configuration:", error);
        alert(error.response?.data?.message || "Failed to create configuration");
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.seat-preview {
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  padding: 16px;
  background: #f5f5f5;
  border-radius: 8px;
}
</style>
